import { store } from '@redux/store';
import uiStateSlice from 'layout/redux/uiStateSlice';
import {
  fromEvent,
  merge,
  filter,
  map,
  distinctUntilChanged,
	tap
} from 'rxjs';
import openLeadModal from 'features/searchResultsExtendedCard';
import { markerHighlight } from 'features/searchResultsMap';
import housingCardConstructor from '../services/getHousingCard';

require('features/leadGenerators').setupLeadGenerators();
require('../analytics/leads');

const listViewContainer = document.querySelector('[data-list-view-element]');
const searchResultsContainer = document.querySelector(
  '[data-search-results-content]'
);
const containerClick$ = fromEvent(searchResultsContainer, 'click');

const isStandardCard = event => !!event.target.closest('[data-standard-card]');
const isAnchorElement = event => event.target instanceof HTMLAnchorElement;
const isFavoriteIcon = event =>
  !!event.target.closest('[data-favorite-trigger]');
const isLeadOpener = event =>
  !!event.target.closest('[data-lead-generator-opener]');
const isInteractiveSitePlan = event =>
  !!event.target.closest('[data-interactive-plan-button]');

const cardClick$ = containerClick$.pipe(
  filter(isStandardCard),
  filter(event => !isFavoriteIcon(event)),
  filter(event => !isLeadOpener(event)),
  filter(event => !isInteractiveSitePlan(event)),
	tap((event) => {
		if (isAnchorElement(event)) {
			event.preventDefault();
		}
	})
);

const leadOpenerClick$ = containerClick$.pipe(
  filter(event => isLeadOpener(event)),
  map(event => event.target.closest('[data-card]'))
);

/**
 * By using event delegation, a listener monitors mouseover events on the standard cards.
 * When a standard card receives the mouseover event, it will store information like
 * the listing id and type od the point of interest that card hovered represents.
 * When mouseover event is not happening on a standard card, then we remove any previous point of
 * interest values from the store.
 */
function onStandardCardHovered(card) {
  if (card) {
    markerHighlight({
      id: card.getAttribute('data-id'),
      communityId: card.getAttribute('data-community-id'),
    });
    return;
  }

  markerHighlight(null);
}

/**
 * Builds a navigation URL by appending query parameters based on card information.
 * 
 * @param {string} url - The base URL to build upon.
 * @param {Object} cardInformation - Information about the card.
 * @returns {string} The complete navigation URL with appropriate query parameters.
 * 
 * @description
 * If the card is marked as a spotlight item, this function adds 'NavigationSource=Spotlight'
 * and 'Position' query parameters to the URL.
 */
const buildNavigationUrl = (url, cardInformation) => {
	const navigationUrl = new URL(url, window.location.origin);
  const { wasMatchedAsSpotlightResult, position } = cardInformation;

	if (wasMatchedAsSpotlightResult) {
		navigationUrl.searchParams.set(
			'NavigationSource',
			'Spotlight'
		);

		navigationUrl.searchParams.set(
			'ResultPosition',
			position
		);
	}

	return navigationUrl.toString();
};

/**
 * Redirects the click on the SRP card to the detial page,
 * and removes the tooltip on the map once the URL is opened.
 */
const redirectClickEventToUrl = (url, cardInformation) => {
	if (!url) {
		return;
	}

	const finalUrl = buildNavigationUrl(url, {
    wasMatchedAsSpotlightResult: cardInformation.wasMatchedAsSpotlightResult === 'true',
		position: +cardInformation.index + 1,
	});

	window.open(finalUrl, '_blank');
	onStandardCardHovered(null);
};

cardClick$.subscribe(event => {
  const standardCardElement = event.target.closest('[data-standard-card]');
  if (!standardCardElement) {
    return;
  }
  const url =
    event.target?.dataset?.url ?? standardCardElement.querySelector('a')?.href;
  const cardInformation = standardCardElement.dataset;
  const googleAnalyticsDTO = housingCardConstructor(
    cardInformation.type,
    url,
    cardInformation
  ).getCardGoogleAnalyticsEvent();

  store.dispatch(uiStateSlice.actions.trackUserEvent({ googleAnalyticsDTO }));
  redirectClickEventToUrl(url, cardInformation);
});

leadOpenerClick$.subscribe(leadOpener =>
  openLeadModal(leadOpener.dataset.id, leadOpener.dataset.type)
);

/**
 * Tracks hover within the list container and guarantees a null emission when
 * the pointer leaves the container entirely (even on very fast mouse moves).
 */
merge(
  fromEvent(listViewContainer, 'mouseover').pipe(
    map(event => event.target?.closest('[data-standard-card]')),
  ),
  fromEvent(listViewContainer, 'mouseleave').pipe(
    map(() => null),
  ),
)
  .pipe(distinctUntilChanged())
  .subscribe(onStandardCardHovered);